import { useCompleteOnboardingStep } from '@doneverse/data/hooks'
import { ONBOARDING_STEP_COPY, isFounderCompletableStep, type OnboardingStepNumber } from '@doneverse/shared'
import { Link } from '@tanstack/react-router'
import { Clock } from 'lucide-react'
import { useOnboardingShell } from './OnboardingShellContext'
import styles from './OnboardingStepCta.module.css'
import { Button } from '@/components/ui/button'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { isStepRouteReachable } from '@/lib/onboardingLock'
import { scrollToHash } from '@/lib/scroll'

/**
 * The one clickable thing for a step. Four shapes, decided by what the step
 * actually is:
 *
 *   * a destination exists and is reachable → a link to it (steps 2, 3, 6, 7,
 *     8, 9, 10);
 *   * its surface is in-shell (step 1's agreement, step 4's product tour) → a
 *     button that opens it;
 *   * the founder is allowed to assert it, and it has neither → a button that
 *     completes it. No step is in that shape today: step 4 has a surface and
 *     step 9 got its page (`/doer-profile`). It survives because
 *     `FOUNDER_COMPLETABLE_STEPS` is a live allowlist, and because a step with
 *     no destination has to author its `cta` as the assertion it is;
 *   * none of those → a waiting state. Step 11 is closed by the workflow when
 *     step 10 closes, and a flag-gated destination is no destination either:
 *     there is nothing for the founder to press, and a dead button would be
 *     worse than saying so. (Step 10 landed here until its checklist got a
 *     route — the launch date gates completion, not visibility.)
 *
 * Its own module because it belongs to neither half of the tracker: the panel
 * (`OnboardingPanel`) is the only thing that draws it, and the surface it opens
 * is mounted by the shell (`OnboardingTracker`). Leaving it in the shell's file
 * would have the page half import the shell half to render one button.
 *
 * Opening a surface is not completing a step: steps 1/3/8 are closed by
 * webhooks (DocuSeal→Keap, Calendly) whatever the founder does on the screen
 * the CTA opened.
 *
 * "Reachable" is {@link isStepRouteReachable} — the same predicate the lock uses
 * to decide where a blocked navigation is sent, so the CTA and the route guard
 * always agree on whether a step has a destination.
 */
export function StepCta({
  step,
  size,
  className,
}: {
  step: OnboardingStepNumber
  size: 'sm' | 'lg'
  className?: string
}) {
  const copy = ONBOARDING_STEP_COPY[step]
  const { hasFlag } = useFeatureFlags()
  const shell = useOnboardingShell()
  const complete = useCompleteOnboardingStep()

  if (copy.route && isStepRouteReachable(copy, hasFlag)) {
    return (
      <Button asChild size={size} className={className}>
        {/* `hash` lands the founder on the part of the page the step is about
            (step 3's booking widget) rather than the top of it. Absent for every
            other step, where the page IS the step. `scrollToHash` covers the
            press made FROM that page, where the link alone is inert — see
            `lib/scroll`. */}
        <Link
          to={copy.route}
          hash={copy.hash}
          onClick={() => {
            if (copy.hash) scrollToHash(copy.hash)
          }}
        >
          {copy.cta}
        </Link>
      </Button>
    )
  }

  // Checked BEFORE the assertion branch, and deliberately falling through to
  // "waiting" rather than to it when there is no shell to host the surface.
  // Step 4 is founder-completable AND carries "Start the tour" — so an assertion
  // button wearing that label would unlock the entire dashboard on a click the
  // founder thought was the beginning of something.
  if (copy.surface) {
    const surface = copy.surface
    if (!shell) return <WaitingForSomeoneElse />
    return (
      <Button size={size} className={className} onClick={() => shell.openSurface(surface)}>
        {copy.cta}
      </Button>
    )
  }

  if (isFounderCompletableStep(step)) {
    return (
      <Button
        size={size}
        className={className}
        disabled={complete.isPending}
        onClick={() => complete.mutate({ step })}
      >
        {copy.cta}
      </Button>
    )
  }

  return <WaitingForSomeoneElse />
}

function WaitingForSomeoneElse() {
  return (
    <span className={styles.waiting}>
      <Clock className={styles.waitingIcon} aria-hidden />
      We&apos;ll move this along as soon as it&apos;s done
    </span>
  )
}
